import { z } from "zod";
import type { UserRef } from "./api-types";

/** Texts, calls, and voicemails (RingCentral). */

export type CommKind = "sms" | "call" | "voicemail" | "fax";

export interface CommItem {
  id: string;
  kind: CommKind;
  direction: "in" | "out";
  otherNumber: string;
  callerName: string | null;
  body: string;
  status: string;
  at: string;
  durationSeconds: number | null;
  transcript: string | null;
  hasAudio: boolean;
  /** A fax: its PDF is at /api/comms/:id/document. */
  hasDocument: boolean;
  pages: number | null;
  contact: { id: string; name: string } | null;
  jobId: string | null;
  read: boolean;
  sentBy: UserRef | null;
  error: string | null;
  /** Staging: it was sent to this test phone instead. */
  redirectedTo: string | null;
}

/** One row per phone number in the inbox: the latest message and how many are unread. */
export interface CommThread {
  otherNumber: string;
  contact: { id: string; name: string } | null;
  /** Several contacts have this number: link it to one. */
  ambiguous: boolean;
  callerName: string | null;
  last: CommItem;
  unread: number;
  /** Blocked from the CRM (Settings → Texts and calls → Blocked numbers). */
  blocked: boolean;
}

/** A number on the blocked list. */
export interface BlockedNumberItem {
  number: string;
  label: string;
  blockedBy: string | null;
  blockedAt: string;
  /** Where it's blocked in RingCentral, line by line; null when this server doesn't change RingCentral (staging). */
  ringcentral: { line: string; ok: boolean; error: string | null }[] | null;
}

export const blockNumberSchema = z.object({ number: z.string().trim().min(7).max(30), label: z.string().trim().min(1, "Say why it's blocked (e.g. Robocaller).").max(200) });

export interface CommStatus {
  /** RingCentral credentials are set up. */
  connected: boolean;
  syncEnabled: boolean;
  lastSyncAt: string | null;
  lastSyncError: string | null;
  /** Numbers the CRM may text right now (staging and development only text test numbers). */
  testNumbers: string[] | null;
  /** Staging: every text goes to this test phone instead. */
  redirectTo: string | null;
  fromNumber: string | null;
  unread: number;
  /** Live events from RingCentral (texts the moment they arrive, and the incoming-call pop-up). */
  live: { active: boolean; expiresAt: string | null; checkedAt: string | null; error: string | null; note: string | null };
  /** This person sees every line ("See all lines"), or only their own extension's. */
  seesAll: boolean;
  /** Their RingCentral extension, if they have one. */
  myExtension: { id: string; number: string; name: string } | null;
}

/** The account's extensions and numbers, to pick from in Settings → Texts and calls. */
export interface CommDirectory {
  extensions: { id: string; number: string; name: string; type: string }[];
  numbers: { number: string; usage: string; extensionId: string | null; features: string[] }[];
}

/** A call popping up for the person whose extension it's ringing. */
export interface CallPopup {
  id: string;
  /** ringing; on_call (they answered); answered_elsewhere (someone else did: shown briefly); missed. */
  state: "ringing" | "on_call" | "answered_elsewhere" | "missed";
  otherNumber: string;
  callerName: string | null;
  /** The number they called (main line, a direct line), when RingCentral says. */
  ourNumber: string | null;
  answeredBy: string | null;
  startedAt: string;
  /** A voicemail they left on this call (once RingCentral has it), to play here. */
  voicemail: { id: string; durationSeconds: number | null; transcript: string | null } | null;
  /** Contacts with this number (empty without permission to see contacts). */
  contacts: { id: string; name: string; type: string; jobs: { id: string; number: number; title: string; address: string; stage: string; open: boolean }[] }[];
}

export const commSendSchema = z.object({
  to: z.string().trim().min(7).max(30),
  body: z.string().trim().min(1, "Write something").max(1000),
  contactId: z.string().uuid().nullable().default(null),
  jobId: z.string().uuid().nullable().default(null),
});

export const commLinkSchema = z.object({ contactId: z.string().uuid() });

export const commLeadSchema = z.object({
  firstName: z.string().trim().max(100).default(""),
  lastName: z.string().trim().max(100).default(""),
  /** Give the new lead a task to call back: assigned to this user. */
  assigneeId: z.string().uuid().nullable().default(null),
});

/** +1XXXXXXXXXX for a US number, else null. */
export function toE164(v: string | null | undefined): string | null {
  const d = (v ?? "").replace(/\D/g, "");
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  return null;
}

/**
 * A reply that opts out of texts (STOP...) or back in (START), per carrier rules: the whole message
 * is the keyword, any case, ignoring punctuation and spaces around it. Anything else is null.
 */
export function optOutKeyword(body: string): { action: "stop" | "start"; keyword: string } | null {
  const word = body
    .trim()
    .replace(/[^A-Za-z]/g, "")
    .toUpperCase();
  if (["STOP", "STOPALL", "UNSUBSCRIBE", "CANCEL", "END", "QUIT", "OPTOUT", "REVOKE"].includes(word)) return { action: "stop", keyword: word };
  if (["START", "UNSTOP"].includes(word)) return { action: "start", keyword: word };
  return null;
}

export interface OptOutStatus {
  optedOut: boolean;
  at: string | null;
  keyword: string | null;
}

/** Contacts copied into RingCentral address books (Settings → Texts and calls). */
export interface ContactSyncStatus {
  enabled: boolean;
  /** Contacts with a phone number (what each address book should have). */
  contacts: number;
  /** Per extension: how many are there now. */
  extensions: { id: string; name: string; copied: number }[];
  lastRunAt: string | null;
  lastError: string | null;
}
